<template>
    <Shell :transition="transition">
        <template #header>
            <div class="pb-2">
                <div class="text-3xl" data-editor="homeTitle">{{ título }}</div>
                <div data-editor="homeWelcomeMessage" v-html="saudação"></div>
            </div>
        </template>

        <div class="px-4 pb-4 space-y-4 flex-grow overflow-auto">
            <div class="bg-white rounded shadow overflow-hidden" v-show="salas.length > 0">
                <div class="p-2 font-bold text-sm text-gray-700">{{ __('Conversas em andamento') }}</div>

                <div
                    v-for="sala in salas"
                    :key="sala.id"
                    class="cursor-pointer p-3 flex items-center hover:bg-gray-100"
                >
                    <div class="w-full flex items-center space-x-2" @click="selecionarSala(sala)">
                        <img :src="roomAvatarUrl(sala)" class="w-10 h-10 rounded-full" />

                        <div>
                            <div class="text-gray-900 flex items-center space-x-1">
                                <span class="truncate">{{ roomTitle(sala) }}</span>

                                <span
                                    v-if="sala.status == 'queued'"
                                    class="
                                        flex-shrink-0
                                        bg-primary-100
                                        text-primary-800
                                        rounded-full
                                        px-2
                                        py-px
                                        text-xs
                                    "
                                >
                                    {{ __(':queue_positionº em fila', { queue_position: roomQueuePosition(sala) }) }}
                                </span>

                                <span
                                    v-if="sala.status == 'closed'"
                                    class="
                                        flex-shrink-0
                                        bg-green-100
                                        text-green-900
                                        rounded-full
                                        px-2
                                        py-px
                                        text-xs
                                        inline-flex
                                        items-center
                                        space-x-1
                                    "
                                >
                                    <alo-icon icon="outline-check" class="w-3 h-3" />
                                    <span>{{ __('Encerrado') }}</span>
                                </span>
                            </div>
                            <div class="text-sm text-gray-600">{{ roomSubtitle(sala) }}</div>
                        </div>
                    </div>

                    <div
                        v-show="unreadEventsCounter(sala) > 0"
                        class="flex-shrink-0 ml-auto bg-red-500 text-xs text-white font-bold py-px px-1 rounded-full"
                    >
                        {{ unreadEventsCounter(sala) }}
                    </div>

                    <div v-if="sala.status == 'closed'" class="flex-shrink-0">
                        <button
                            class="p-1 hover:bg-gray-200 rounded"
                            :title="__('Remover da lista')"
                            @click="removeRoomFromUI(sala)"
                        >
                            <alo-icon icon="outline-x" class="w-6 h-6 text-gray-400" />
                        </button>
                    </div>
                </div>
            </div>

            <div class="bg-white rounded p-4 shadow" v-show="loadingAvailability">
                <div class="flex items-center justify-center space-x-2 p-4">
                    <alo-loading-ring class="w-5 h-5" />
                    <span class="text-gray-600">{{ __('Carregando...') }}</span>
                </div>
            </div>

            <div class="bg-white rounded p-4 shadow" v-show="atLeastOneDepartmentAvailable && canStartAnotherRoom">
                <div class="font-bold">{{ __('Novo atendimento') }}</div>

                <div class="mt-1 mb-4 text-sm">
                    <div v-show="availableDepartments.length == 1">
                        {{ __('O departamento') }}
                        <span
                            v-for="dept in availableDepartments"
                            :key="dept.id"
                            class="bg-gray-100 text-black mr-1 p-0.5 pl-1 rounded"
                        >
                            {{ dept.nome }}
                        </span>
                        {{ __('está disponível.') }}
                    </div>

                    <div
                        v-show="availableDepartments.length > 1"
                        class="cursor-pointer text-gray-800"
                        :class="[departmentsCollapsed ? 'truncate' : '']"
                        @click="departmentsCollapsed = !departmentsCollapsed"
                    >
                        {{ __('Departamentos disponíveis: ') }}
                        <span
                            v-for="dept in availableDepartments"
                            :key="dept.id"
                            class="bg-gray-100 text-black mr-1 p-0.5 pl-1 rounded"
                        >
                            {{ dept.nome }}
                        </span>
                    </div>
                </div>

                <div>
                    <PrimaryButton @click="iniciarConversa()">
                        <span>{{ __('Iniciar uma conversa') }}</span>

                        <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M17 8l4 4m0 0l-4 4m4-4H3"
                            />
                        </svg>
                    </PrimaryButton>
                </div>
            </div>

            <div class="bg-white rounded p-4 shadow" v-show="!loadingAvailability && !atLeastOneDepartmentAvailable">
                <div class="rich-text" v-html="unavailableContent.html"></div>
            </div>

            <div v-show="baseDeConhecimentoHabilitada">
                <div class="bg-white rounded p-4 shadow">
                    <div class="rich-text" data-editor="searchTitle" v-html="títuloDaBaseDeConhecimento.html"></div>

                    <form @submit.prevent="goToKnowledgeDatabaseSearch()" class="relative">
                        <input v-model="search" class="input pl-7" :placeholder="__('Pesquisar...')" />

                        <div class="absolute inset-y-0 left-0 flex items-center">
                            <button class="flex-shrink-0 px-2">
                                <svg
                                    class="w-4 h-4 text-gray-600"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                >
                                    <path
                                        fill-rule="evenodd"
                                        d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                                        clip-rule="evenodd"
                                    />
                                </svg>
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    </Shell>
</template>

<script>
import Shell from './Shell.vue'
import PrimaryButton from './PrimaryButton'
import { userProfile, AMBIENTE_EDITOR } from '../Store/Store'

export default {
    props: ['transition'],
    components: { Shell, PrimaryButton },
    data() {
        return {
            departmentsCollapsed: true,
        }
    },
    async mounted() {
        if (this.runningInEditor) return

        if (!this.$store.state.departmentsAvailability) {
            await this.$store.dispatch('loadDepartamentsAvailability')
        }
    },
    computed: {
        cor() {
            return this.$store.getters.corPrimária
        },

        runningInEditor() {
            return this.$store.state.ambiente == AMBIENTE_EDITOR
        },

        título() {
            return this.$store.state.aparência.homeTitle || __('Olá 👋')
        },
        saudação() {
            let welcome = this.$store.state.aparência.homeWelcomeMessage || __('Como podemos te ajudar?')
            if (welcome.html) return welcome.html
            return welcome
        },

        salas() {
            return this.$store.state.salasByTab['chat']
        },

        títuloDaBaseDeConhecimento() {
            return this.$store.state.aparência.searchTitle || __('Encontre sua dúvida em nosso FAQ')
        },

        baseDeConhecimentoHabilitada() {
            return this.$store.state.aparência.baseDeConhecimento != null
        },

        unavailableContent() {
            return this.$store.state.aparência.unavailableContent
        },

        /**
         * Estamos carregando o status de disponibilidade dos departamentos?
         */
        loadingAvailability() {
            return this.$store.state.departmentsAvailability === null
        },

        /**
         * Departamentos disponíveis para atendimento.
         */
        availableDepartments() {
            return this.$store.getters.availableDepartments
        },

        /**
         * Pelo menos um departamento desse widget está disponível para atendimento?
         * O workspace está em horário de trabalho?
         */
        atLeastOneDepartmentAvailable() {
            return this.$store.getters.availableDepartments.length > 0 && this.$store.getters.estamosEmHorarioDeTrabalho
        },

        /**
         * O contato pode iniciar mais uma atendimento?
         *
         * @return {boolean}
         */
        canStartAnotherRoom() {
            return this.$store.getters.canStartAnotherRoom
        },

        search: {
            get() {
                return this.$store.state.search
            },

            set(value) {
                this.$store.commit('updateSearch', value)
            },
        },
    },
    methods: {
        iniciarConversa() {
            if (this.$store.state.aparência.askForIdentification) {
                this.$store.dispatch('transiçãoParaDireita', 'identificação')
            } else {
                this.$store.dispatch('transiçãoParaDireita', 'conversa')
            }
        },
        selecionarSala(sala) {
            this.$store.dispatch('selecionarSala', sala)
        },
        unreadEventsCounter(sala) {
            return this.$store.getters.eventosNãoLidosDaSala(sala.id).length
        },
        goToKnowledgeDatabaseSearch() {
            if (!this.search) return

            this.$store.dispatch('transiçãoParaDireita', 'search')
            this.$store.dispatch('searchForQuestions')
        },

        roomAvatarUrl(room) {
            let userId = this.$store.getters.roomUserId(room.id)
            if (userId) {
                let url = userProfile(userId).url_avatar_128px
                if (url) return url
            }

            return this.$store.state.aparência.chatDefaultAvatar
        },

        roomTitle(room) {
            let userId = this.$store.getters.roomUserId(room.id)

            if (userId) {
                return userProfile(userId).name || __('Carregando...')
            }

            return this.$store.state.aparência.chatDefaultHeaderTitle
        },

        roomSubtitle(room) {
            let departament = this.$store.getters.findDepartamentById(room.department_id)

            if (departament) {
                return departament.nome
            }
        },

        roomQueuePosition(room) {
            return this.$store.getters.queuePosition(room.id)
        },

        /**
         * Remove o atendimento da interface.
         */
        removeRoomFromUI(room) {
            this.$store.commit('deleteChatRoom', room.id)
        },
    },
}
</script>